/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import * as classes from './styles'

export type TempValueWrapperProps<Value> = {
  value: Value
  onChange: (value: Value) => void
  children: (props: { value: Value; onChange: (value: Value) => void; editable: boolean }) => JSX.Element
  initialEditable?: boolean
  disabled?: boolean
  className?: string
}

export function TempValueWrapper<Value>({
  value,
  onChange,
  children,
  initialEditable = false,
  disabled,
  className,
}: TempValueWrapperProps<Value>) {
  const [editable, setEditable] = useState(initialEditable)
  const [tempValue, setTempValue] = useState<Value>(value)

  useEffect(() => {
    setTempValue(value)
  }, [value])

  const handleCancel = useCallback(() => {
    setEditable(false)
    setTempValue(value)
  }, [value])

  const handleConfirm = useCallback(() => {
    setEditable(false)
    onChange(tempValue)
  }, [onChange, tempValue])

  return (
    <div className={className} css={classes.wrapper}>
      {children({
        value: tempValue,
        onChange: editable ? setTempValue : onChange,
        editable,
      })}
      <div className="buttons-wrapper">
        {!editable ? (
          <Button
            variant="outlined"
            color="gray"
            size="small"
            onClick={() => setEditable(true)}
            disabled={disabled}
            css={classes.iconButton}
            data-testid="edit-button"
          >
            <EditIcon />
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              color="red"
              size="small"
              onClick={handleCancel}
              css={classes.iconButton}
              data-testid="cancel-button"
            >
              <CrossIcon />
            </Button>
            <Button
              variant="outlined"
              color="green"
              size="small"
              onClick={handleConfirm}
              css={classes.iconButton}
              data-testid="check-button"
            >
              <CheckIcon />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
